import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage/";
import { useAnimation } from "../../useAnimation";
import {
  ChapterCuePointsResponse,
  StoryboardHighlights,
  Storyboard,
} from "pages/media/types/";
import { useCallback } from "react";
import { ExtendedSectionHighlight } from "../../types";
import {
  reconstructStoryboardHighlights,
  reconstructStoryboardHighlightsWithoutStoryboard,
} from "../../helpers/helpers";
import {
  removeHighlightById,
  stripOrphanHighlightAnchors,
} from "pages/media/helpers/scriptAnchors";
import { useHighlightsStore } from "../../../animator/useHighlightsStore";
import { useCuepointsStore } from "../../../animator/useCuepointsStore";
import { useFetchChaptersWithCuepoints } from "../../helpers/shared/fetchChaptersWithCuepoints";
import { storyboardToStoryboardDetailed } from "../../../storyboard/editor/helpers";

export const useSaveAnimation = (
  projectId: string,
  mediaId: string,
  storyboardId: string,
  setNewSectionHighlight?: (highlight?: ExtendedSectionHighlight) => void
) => {
  const { refetchProjectAssets, saveProjectAsset } = useMediaAssetsStorage({
    projectId,
  });

  const { highlightsAssetId, hasUploadedVideo, documentId, timingChapters } =
    useAnimation(projectId, mediaId);
  const { highlights, sectionHighlights, highlightedStoryboard } =
    useHighlightsStore();

  const fetchChaptersWithCuepoints =
    useFetchChaptersWithCuepoints(timingChapters);

  const { cuepoints } = useCuepointsStore();

  const addCuepointsToHighlightsAssetObject = (
    preliminaryHighlightsAssetObject: StoryboardHighlights,
    backendChapters: ChapterCuePointsResponse[]
  ) => {
    const highlightsAssetObject = { ...preliminaryHighlightsAssetObject };
    if (backendChapters) {
      const reconstructSectionMap = highlightsAssetObject?.section_map;
      backendChapters?.forEach((chapter) => {
        let sectionId;
        Object.entries(reconstructSectionMap).forEach(([key, value]) => {
          if (value === chapter?.chapter_name) {
            sectionId = key;
          }
        });
        if (sectionId) {
          const section = highlightsAssetObject.section_highlights[sectionId];
          // Drop cuepoints the BE derived from script anchors that have no
          // matching highlight+source persisted — saving them would create
          // orphan cuepoints in the asset.
          const validHighlightIds = new Set(
            section?.highlights?.map((h) => h.id) ?? []
          );
          highlightsAssetObject.section_highlights[sectionId].cuepoints =
            chapter.cuepoints?.cuepoints?.filter((cp) =>
              validHighlightIds.has(cp.highlight_id)
            );
        }
      });
    }
    return highlightsAssetObject;
  };

  const saveAnimation = useCallback(
    async (
      reconstructHighlightsAsset: StoryboardHighlights,
      storyboardToSave?: Storyboard
    ) => {
      if (!hasUploadedVideo && !storyboardToSave?.sections?.length) {
        console.error("Attempted to save invalid storyboard!", {
          hasStoryboard: !!storyboardToSave,
          hasSections: !!storyboardToSave?.sections,
          sectionsLength: storyboardToSave?.sections?.length,
        });
        throw new Error("Cannot save storyboard without sections");
      }
      const detailedStoryboard =
        storyboardToStoryboardDetailed(storyboardToSave);
      await Promise.all([
        saveProjectAsset(
          "Highlights",
          {
            document_id: reconstructHighlightsAsset.document_id,
            section_map: reconstructHighlightsAsset.section_map,
            section_highlights: reconstructHighlightsAsset?.section_highlights,
            // BE recomputes section_timestamps on regeneration; echo the
            // current value so the PATCH doesn't null it out.
            section_timestamps: reconstructHighlightsAsset?.section_timestamps,
          },
          highlightsAssetId
        ),
        ...(hasUploadedVideo
          ? []
          : [saveProjectAsset("Storyboard", detailedStoryboard, storyboardId)]),
      ]);

      await refetchProjectAssets();
      setNewSectionHighlight?.();
    },
    [
      hasUploadedVideo,
      saveProjectAsset,
      refetchProjectAssets,
      setNewSectionHighlight,
      highlightsAssetId,
      storyboardId,
    ]
  );

  const saveUpdatedData = useCallback(
    async (
      storyboardHighlights?: StoryboardHighlights,
      newSectionHighlight?: ExtendedSectionHighlight | null
    ) => {
      if (hasUploadedVideo) {
        const reconstructHighlightsAsset =
          reconstructStoryboardHighlightsWithoutStoryboard(
            sectionHighlights,
            highlights,
            cuepoints,
            documentId,
            storyboardHighlights?.section_timestamps
          );
        await saveAnimation(reconstructHighlightsAsset, highlightedStoryboard);
      } else {
        // Strip any HL anchors left behind in the script for ids that no
        // longer exist in the sectionHighlights store (orphans from aborted
        // pick flows, unmounts, or delete races). Without this, the BE would
        // derive cuepoints from those stale anchors at save time.
        const validSectionHighlightIds = new Set(
          sectionHighlights?.map((h) => h.id) ?? []
        );
        const reconciledStoryboard = highlightedStoryboard
          ? {
              ...highlightedStoryboard,
              sections: highlightedStoryboard.sections.map((section) => ({
                ...section,
                script: {
                  ...section.script,
                  content: stripOrphanHighlightAnchors(
                    section.script?.content ?? "",
                    validSectionHighlightIds
                  ),
                },
              })),
            }
          : highlightedStoryboard;
        const detailedStoryboard =
          storyboardToStoryboardDetailed(reconciledStoryboard);
        const reconstructHighlightsAsset = reconstructStoryboardHighlights(
          sectionHighlights,
          highlights,
          cuepoints,
          storyboardHighlights,
          detailedStoryboard
        );
        // if a word on the script was picked (tags were added to storyboard and newSectionHighlight is not undefined),
        // but highlight wasn't created, we need to remove tags from storyboard
        const storyboardToSave = newSectionHighlight
          ? {
              ...detailedStoryboard,
              sections: detailedStoryboard.sections.map((section) => ({
                ...section,
                script: {
                  ...section.script,
                  content: removeHighlightById(
                    section.script.content,
                    newSectionHighlight.id
                  ),
                },
              })),
            }
          : reconciledStoryboard;

        const backendChapters =
          await fetchChaptersWithCuepoints(storyboardToSave);

        const highlightsAssetWithCuepoints =
          addCuepointsToHighlightsAssetObject(
            reconstructHighlightsAsset,
            backendChapters
          );
        await saveAnimation(highlightsAssetWithCuepoints, storyboardToSave);
      }
    },
    [
      hasUploadedVideo,
      sectionHighlights,
      highlights,
      cuepoints,
      documentId,
      highlightedStoryboard,
      fetchChaptersWithCuepoints,
      saveAnimation,
    ]
  );
  return saveUpdatedData;
};
